import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Shield, Search, Building2, Home, ChevronDown, ChevronRight,
  Check, X, Minus, Save, AlertCircle, Loader2
} from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { Card } from '../components/ui/Card';
import api from '../services/api';
import { BackHeader } from '../components/BackHeader';

// ── Types ──────────────────────────────────────────────────────────────────

type AccessLevel = 'allow' | 'deny' | 'inherit';

interface PermFunction {
  id: string;
  name: string;
  module: string;
  description?: string;
  defaultAccess: Record<string, AccessLevel>;
}

interface MatrixEntry {
  accessLevel: AccessLevel;
  isOverride: boolean;
  defaultLevel: AccessLevel;
}

interface PermissionMatrix {
  functions: PermFunction[];
  matrix: Record<string, Record<string, MatrixEntry>>;
}

interface Condo {
  id: string;
  name: string;
}

interface Unit {
  id: string;
  label: string;
}

// ── Constants ──────────────────────────────────────────────────────────────

const MODULES = [
  'CONDO_MGMT', 'DASHBOARD', 'RESIDENTS', 'FINANCE', 'MOROSOS',
  'AMENITIES', 'CALENDAR', 'QUEJAS', 'ANUNCIOS', 'VOTING',
  'QR', 'PANIC', 'SECURITY', 'HOME', 'SUPPLIER', 'PROFILE',
  'CONDO_INFO', 'COMMUNICATION',
] as const;

const MODULE_LABELS: Record<string, string> = {
  CONDO_MGMT: 'Condo Management',
  DASHBOARD: 'Dashboard',
  RESIDENTS: 'Residents',
  FINANCE: 'Finance',
  MOROSOS: 'Morosos',
  AMENITIES: 'Amenities',
  CALENDAR: 'Calendar',
  QUEJAS: 'Quejas y Sugerencias',
  ANUNCIOS: 'Anuncios',
  VOTING: 'Voting',
  QR: 'QR Access',
  PANIC: 'Panic Button',
  SECURITY: 'Security',
  HOME: 'Home',
  SUPPLIER: 'Suppliers',
  PROFILE: 'Profile',
  CONDO_INFO: 'Condo Info',
  COMMUNICATION: 'Communication',
};

const ROLES: { key: string; label: string }[] = [
  { key: 'super_admin', label: 'Super Admin' },
  { key: 'admin', label: 'Admin' },
  { key: 'sub_admin', label: 'Sub Admin' },
  { key: 'treasurer', label: 'Directivo' },
  { key: 'propietario', label: 'Propietario' },
  { key: 'resident', label: 'Resident' },
  { key: 'familiar', label: 'Familiar' },
  { key: 'empleado', label: 'Empleado' },
  { key: 'maintenance_staff', label: 'Mantenimiento' },
  { key: 'security', label: 'Seguridad' },
  { key: 'provider', label: 'Provider' },
];

// Roles a propietario can toggle in unit-level view
const PROPIETARIO_EDITABLE_ROLES = ['familiar', 'empleado', 'resident'];

// ── Component ──────────────────────────────────────────────────────────────

export default function PermissionManager() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = user?.role === 'admin' || isSuperAdmin;
  const isSubAdmin = user?.role === 'sub_admin';
  const isAdminOrSubAdmin = isAdmin || isSubAdmin;
  const isPropietario = user?.role === 'propietario';

  // View state
  const [viewMode, setViewMode] = useState<'global' | 'condo'>(isSuperAdmin ? 'global' : 'condo');
  const [condos, setCondos] = useState<Condo[]>([]);
  const [selectedCondoId, setSelectedCondoId] = useState<string>('');
  const [units, setUnits] = useState<Unit[]>([]);
  const [selectedUnitId, setSelectedUnitId] = useState<string>('');

  // Data state
  const [matrix, setMatrix] = useState<PermissionMatrix | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<Set<string>>(new Set());
  const [saveError, setSaveError] = useState<string | null>(null);

  // UI state
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState<string>('all');
  const [highlightedRole, setHighlightedRole] = useState<string>('');
  const [collapsedModules, setCollapsedModules] = useState<Set<string>>(new Set());

  // ── Load condos ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (isSuperAdmin) {
      api.get('/condos').then(res => {
        const data = res.data?.data?.items || res.data?.data || res.data || [];
        const mapped = Array.isArray(data) ? data.map((c: any) => ({ id: c.id, name: c.name })) : [];
        setCondos(mapped);
      }).catch(() => {
        setCondos([]);
      });
    } else if (user?.condoId) {
      setSelectedCondoId(user.condoId);
      setViewMode('condo');
    }
  }, [isSuperAdmin, user?.condoId]);

  // ── Load units when condo changes ────────────────────────────────────────

  useEffect(() => {
    if (!selectedCondoId) {
      setUnits([]);
      return;
    }
    api.get(`/condos/${selectedCondoId}/units`).then(res => {
      const data = res.data?.data?.items || res.data?.data || res.data || [];
      const mapped = Array.isArray(data) ? data.map((u: any) => ({ id: u.id, label: u.label || u.unit_number || u.name || 'N/A' })) : [];
      setUnits(mapped);
    }).catch(() => setUnits([]));
  }, [selectedCondoId]);

  // ── Load matrix ──────────────────────────────────────────────────────────

  const loadMatrix = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let url = '/permissions/matrix';
      if (viewMode === 'condo' && selectedCondoId) {
        url = `/permissions/matrix/${selectedCondoId}`;
        if (selectedUnitId) {
          url = `/permissions/matrix/${selectedCondoId}/units/${selectedUnitId}`;
        }
      }

      const [functionsRes, matrixRes] = await Promise.all([
        api.get('/permissions/functions').catch(() => null),
        api.get(url).catch(() => null),
      ]);

      // API contract (bug #149, verified 2026-09-24 against
      // api/src/modules/permissions/routes.js + database/permissions.js):
      // - GET /permissions/functions -> { functions: [{ functionId, module, name,
      //   defaultRoles: string[] }], total, roles }  (no { success, data } wrapper)
      // - GET /permissions/matrix[/...] -> { functions: [{ functionId, module,
      //   name, roles: { [role]: 'allow' | 'deny' } }], roles }
      //   The matrix returns RESOLVED levels (defaults + overrides merged) and
      //   does NOT expose an override flag.
      const functionsData: any[] =
        functionsRes?.data?.data?.functions || functionsRes?.data?.functions || [];
      const matrixData = matrixRes?.data?.data || matrixRes?.data || {};
      const matrixFunctions: any[] = Array.isArray(matrixData.functions)
        ? matrixData.functions
        : [];

      // Use the matrix functions as the primary source; fall back to the
      // functions endpoint (defaults only) if the matrix call failed.
      const entries: any[] = matrixFunctions.length
        ? matrixFunctions
        : functionsData.map((f: any) => ({ ...f, roles: undefined }));

      const fnList: PermFunction[] = [];
      const matrixMap: Record<string, Record<string, MatrixEntry>> = {};

      entries.forEach((f: any) => {
        const fid = f.functionId || f.id || f.code;
        if (!fid) return;
        const meta = functionsData.find((x: any) => x.functionId === fid) || f;
        const defaultRoles: string[] = meta.defaultRoles || f.defaultRoles || [];
        const apiRoles = f.roles || {};

        const defaultAccess: Record<string, AccessLevel> = {};
        ROLES.forEach(r => {
          defaultAccess[r.key] = defaultRoles.includes(r.key) ? 'allow' : 'deny';
        });

        fnList.push({
          id: fid,
          name: f.name || meta.name || fid,
          module: f.module || 'OTHER',
          description: f.description || meta.description,
          defaultAccess,
        });

        matrixMap[fid] = {};
        ROLES.forEach(role => {
          const defaultLevel = defaultAccess[role.key];
          const apiLevel = apiRoles[role.key] as AccessLevel | undefined;
          // No override flag from the API: mark a cell as an override when its
          // resolved level differs from the built-in role default.
          const isOverride = apiLevel !== undefined && apiLevel !== defaultLevel;
          matrixMap[fid][role.key] = {
            accessLevel: apiLevel || defaultLevel,
            isOverride,
            defaultLevel,
          };
        });
      });

      if (fnList.length === 0) {
        throw new Error('Permission endpoints returned no functions');
      }

      setMatrix({ functions: fnList, matrix: matrixMap });

    } catch (err) {
      console.error('Error loading permission matrix:', err);
      setError('No se pudo cargar la matriz de permisos. Verifique su conexión.');
      setMatrix(null);
    } finally {
      setLoading(false);
    }
  }, [viewMode, selectedCondoId, selectedUnitId]);

  useEffect(() => {
    if (viewMode === 'global' && !isSuperAdmin) return;
    if (viewMode === 'condo' && !selectedCondoId) return;
    loadMatrix();
  }, [loadMatrix, viewMode, selectedCondoId, isSuperAdmin]);

  // ── Save override ────────────────────────────────────────────────────────

  const saveOverride = useCallback(async (
    functionId: string,
    role: string,
    accessLevel: AccessLevel,
  ) => {
    if (!matrix) return;
    const key = `${functionId}:${role}`;
    setSaving(prev => new Set(prev).add(key));
    setSaveError(null);

    try {
      if (accessLevel === 'inherit') {
        // Delete override
        await api.delete('/permissions/override', {
          data: {
            condoId: viewMode === 'condo' ? selectedCondoId : undefined,
            unitId: selectedUnitId || undefined,
            role,
            functionId,
          },
        });
      } else {
        // Set override
        await api.put('/permissions/override', {
          condoId: viewMode === 'condo' ? selectedCondoId : undefined,
          unitId: selectedUnitId || undefined,
          role,
          functionId,
          accessLevel,
        });
      }

      // Update local state
      setMatrix(prev => {
        if (!prev) return prev;
        const next = { ...prev, matrix: { ...prev.matrix } };
        const fnMatrix = { ...next.matrix[functionId] };
        fnMatrix[role] = {
          accessLevel: accessLevel === 'inherit'
            ? fnMatrix[role].defaultLevel
            : accessLevel,
          isOverride: accessLevel !== 'inherit',
          defaultLevel: fnMatrix[role].defaultLevel,
        };
        next.matrix[functionId] = fnMatrix;
        return next;
      });
    } catch (err) {
      console.error('Error saving override:', err);
      setSaveError('Error al guardar el permiso. Intente nuevamente.');
    } finally {
      setSaving(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }, [matrix, viewMode, selectedCondoId, selectedUnitId]);

  // ── Cycle cell ───────────────────────────────────────────────────────────

  const cycleCell = useCallback((functionId: string, role: string) => {
    if (!matrix) return;
    const entry = matrix.matrix[functionId]?.[role];
    if (!entry) return;

    // Propietario can only edit certain roles
    if (isPropietario && !PROPIETARIO_EDITABLE_ROLES.includes(role)) return;

    const cycleOrder: AccessLevel[] = ['inherit', 'allow', 'deny'];
    const currentIdx = entry.isOverride
      ? cycleOrder.indexOf(entry.accessLevel)
      : 0; // inherit
    const nextLevel = cycleOrder[(currentIdx + 1) % cycleOrder.length];
    saveOverride(functionId, role, nextLevel);
  }, [matrix, isPropietario, saveOverride]);

  // ── Filtered functions ──────────────────────────────────────────────────

  const filteredFunctions = useMemo(() => {
    if (!matrix) return [];
    return matrix.functions.filter(fn => {
      if (moduleFilter !== 'all' && fn.module !== moduleFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!fn.name.toLowerCase().includes(q) && !fn.id.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [matrix, search, moduleFilter]);

  // Group by module
  const groupedFunctions = useMemo(() => {
    const groups: Record<string, PermFunction[]> = {};
    filteredFunctions.forEach(fn => {
      if (!groups[fn.module]) groups[fn.module] = [];
      groups[fn.module].push(fn);
    });
    return groups;
  }, [filteredFunctions]);

  const moduleOrder = MODULES.filter(m => groupedFunctions[m]?.length > 0);

  // ── Toggle collapse ──────────────────────────────────────────────────────

  const toggleModule = (module: string) => {
    setCollapsedModules(prev => {
      const next = new Set(prev);
      if (next.has(module)) next.delete(module);
      else next.add(module);
      return next;
    });
  };

  // ── Cell rendering ───────────────────────────────────────────────────────

  const renderCell = (functionId: string, role: string) => {
    const entry = matrix?.matrix[functionId]?.[role];
    if (!entry) return <td key={role} className="border border-[var(--v-surface-3)] px-1 py-1 text-center">—</td>;

    const canEdit = isPropietario
      ? PROPIETARIO_EDITABLE_ROLES.includes(role)
      : isAdminOrSubAdmin;

    const isSaving = saving.has(`${functionId}:${role}`);
    const effective = entry.isOverride ? entry.accessLevel : entry.defaultLevel;

    const bgColor = effective === 'allow'
      ? 'bg-green-600/30 hover:bg-green-600/50'
      : effective === 'deny'
        ? 'bg-red-600/30 hover:bg-red-600/50'
        : 'bg-slate-700/40 hover:bg-slate-600/50';

    const icon = effective === 'allow'
      ? <Check className="w-4 h-4 text-green-400" />
      : effective === 'deny'
        ? <X className="w-4 h-4 text-red-400" />
        : <Minus className="w-4 h-4 text-slate-400" />;

    const tooltip = entry.isOverride
      ? `Override: ${effective.toUpperCase()} (default: ${entry.defaultLevel})`
      : `Default: ${entry.defaultLevel.toUpperCase()}`;

    return (
      <td
        key={role}
        className={`border border-[var(--v-surface-3)] px-1 py-1 text-center ${bgColor} ${canEdit ? 'cursor-pointer' : 'cursor-default opacity-70'} ${highlightedRole === role ? 'ring-2 ring-teal-400' : ''} transition-colors`}
        onClick={canEdit ? () => cycleCell(functionId, role) : undefined}
        title={tooltip}
      >
        {isSaving ? (
          <Loader2 className="w-4 h-4 text-teal-400 animate-spin mx-auto" />
        ) : (
          icon
        )}
      </td>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────

  if (!isAdminOrSubAdmin && !isPropietario) {
    return (
      <div className="p-6 bg-[var(--v-surface-1)] min-h-[60vh] flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <Shield className="w-12 h-12 text-slate-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[var(--v-text-primary)] mb-2">Sin acceso a este módulo</h2>
          <p className="text-slate-400">No tiene permisos para gestionar permisos.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 h-full overflow-auto bg-[var(--v-surface-1)] text-slate-100">
      <BackHeader fallback="/admin" breadcrumb='Administración › Permisos' />
      <div className="max-w-[1400px] mx-auto">
        <PageHeader
          title="Permission Manager — Vecindus Platform"
          subtitle={`${user?.name || 'Usuario'} · ${user?.role || 'N/A'}`}
        />

        {/* Controls Bar */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          {/* View toggle (super_admin only sees global option) */}
          {isSuperAdmin && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setViewMode('global'); setSelectedUnitId(''); }}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${viewMode === 'global' ? 'bg-teal-600 text-white' : 'bg-[var(--v-surface-1)] text-slate-400 hover:bg-slate-700'}`}
              >
                Global
              </button>
              <button
                onClick={() => setViewMode('condo')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${viewMode === 'condo' ? 'bg-teal-600 text-white' : 'bg-[var(--v-surface-1)] text-slate-400 hover:bg-slate-700'}`}
              >
                Per-Condo
              </button>
            </div>
          )}

          {/* Condo selector */}
          {viewMode === 'condo' && (
            <select
              value={selectedCondoId}
              onChange={(e) => { setSelectedCondoId(e.target.value); setSelectedUnitId(''); }}
              className="bg-[var(--v-surface-1)] border border-[var(--v-surface-3)] rounded-lg px-3 py-1.5 text-sm text-slate-200"
            >
              <option value="">Select Condo…</option>
              {condos.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
              {/* If user already has a condo (non-super_admin), show it */}
              {!isSuperAdmin && user?.condoId && !condos.find(c => c.id === user.condoId) && (
                <option value={user.condoId}>My Condo</option>
              )}
            </select>
          )}

          {/* Unit selector (only in condo view) */}
          {viewMode === 'condo' && selectedCondoId && units.length > 0 && (
            <select
              value={selectedUnitId}
              onChange={(e) => setSelectedUnitId(e.target.value)}
              className="bg-[var(--v-surface-1)] border border-[var(--v-surface-3)] rounded-lg px-3 py-1.5 text-sm text-slate-200"
            >
              <option value="">All Units (Condo-level)</option>
              {units.map(u => (
                <option key={u.id} value={u.id}>Unit {u.label}</option>
              ))}
            </select>
          )}

          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              placeholder="Search function…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-[var(--v-surface-1)] border border-[var(--v-surface-3)] rounded-lg pl-9 pr-3 py-1.5 text-sm text-slate-200 placeholder-slate-500"
            />
          </div>

          {/* Module filter */}
          <select
            value={moduleFilter}
            onChange={(e) => setModuleFilter(e.target.value)}
            className="bg-[var(--v-surface-1)] border border-[var(--v-surface-3)] rounded-lg px-3 py-1.5 text-sm text-slate-200"
          >
            <option value="all">All Modules</option>
            {MODULES.map(m => (
              <option key={m} value={m}>{MODULE_LABELS[m] || m}</option>
            ))}
          </select>
        </div>

        {/* Save error */}
        {saveError && (
          <div className="mb-3 flex items-center gap-2 px-4 py-2 bg-red-900/40 border border-red-700 rounded-lg text-red-300 text-sm">
            <AlertCircle className="w-4 h-4" /> {saveError}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-3 flex items-center gap-2 px-4 py-2 bg-red-900/40 border border-red-700 rounded-lg text-red-300 text-sm">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
            <span className="ml-3 text-slate-400">Loading permission matrix…</span>
          </div>
        )}

        {/* Matrix Table */}
        {!loading && matrix && (
          <>
            {/* Legend */}
            <div className="flex items-center gap-4 mb-3 text-xs text-slate-400">
              <span className="flex items-center gap-1"><Check className="w-3 h-3 text-green-400" /> Allow</span>
              <span className="flex items-center gap-1"><X className="w-3 h-3 text-red-400" /> Deny</span>
              <span className="flex items-center gap-1"><Minus className="w-3 h-3 text-slate-400" /> Inherit (default)</span>
              <span className="text-slate-500">· Click cell to cycle · Changes auto-save</span>
              {isPropietario && (
                <span className="text-amber-400">· You can only edit: Familiar, Empleado, Resident</span>
              )}
            </div>

            <div className="overflow-x-auto border border-[var(--v-surface-3)] rounded-lg" style={{ maxHeight: '70vh' }}>
              <table className="border-collapse text-sm" style={{ minWidth: '900px' }}>
                {/* Sticky header */}
                <thead className="sticky top-0 z-20">
                  <tr className="bg-[var(--v-surface-1)]">
                    <th className="sticky left-0 z-30 bg-[var(--v-surface-1)] border border-[var(--v-surface-3)] px-2 py-2 text-left text-slate-300 font-semibold w-12">#</th>
                    <th className="sticky left-12 z-30 bg-[var(--v-surface-1)] border border-[var(--v-surface-3)] px-3 py-2 text-left text-slate-300 font-semibold min-w-[200px]">Function</th>
                    {ROLES.map(role => (
                      <th
                        key={role.key}
                        className={`border border-[var(--v-surface-3)] px-2 py-2 text-center text-xs font-semibold cursor-pointer select-none ${highlightedRole === role.key ? 'text-teal-400 bg-teal-900/30' : 'text-slate-300 hover:text-teal-300'}`}
                        onClick={() => setHighlightedRole(highlightedRole === role.key ? '' : role.key)}
                        title={`Click to highlight ${role.label} column`}
                      >
                        {role.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {moduleOrder.map(moduleName => {
                    const fns = groupedFunctions[moduleName] || [];
                    const isCollapsed = collapsedModules.has(moduleName);
                    return (
                      <React.Fragment key={moduleName}>
                        {/* Module banner row */}
                        <tr
                          className="bg-teal-900/40 cursor-pointer hover:bg-teal-800/40"
                          onClick={() => toggleModule(moduleName)}
                        >
                          <td className="sticky left-0 z-10 bg-teal-900/40 border border-[var(--v-surface-3)] px-2 py-1.5">
                            {isCollapsed ? <ChevronRight className="w-4 h-4 text-teal-400" /> : <ChevronDown className="w-4 h-4 text-teal-400" />}
                          </td>
                          <td
                            className="sticky left-12 z-10 bg-teal-900/40 border border-[var(--v-surface-3)] px-3 py-1.5 text-teal-300 font-bold"
                            colSpan={ROLES.length + 1}
                          >
                            {MODULE_LABELS[moduleName] || moduleName}
                            <span className="ml-2 text-xs text-teal-500">({fns.length} functions)</span>
                          </td>
                        </tr>
                        {/* Function rows */}
                        {!isCollapsed && fns.map((fn, idx) => (
                          <tr key={fn.id} className="hover:bg-[var(--v-surface-1)]/50">
                            <td className="sticky left-0 z-10 bg-[var(--v-surface-1)] border border-[var(--v-surface-3)] px-2 py-1 text-xs text-slate-500 text-right">{idx + 1}</td>
                            <td className="sticky left-12 z-10 bg-[var(--v-surface-1)] border border-[var(--v-surface-3)] px-3 py-1 text-slate-200">
                              <div className="font-medium">{fn.name}</div>
                              <div className="text-xs text-slate-500">{fn.id}</div>
                            </td>
                            {ROLES.map(role => renderCell(fn.id, role.key))}
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Empty state */}
            {filteredFunctions.length === 0 && (
              <div className="text-center py-20 text-slate-500">
                <Shield className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No functions match your search/filter.</p>
              </div>
            )}
          </>
        )}

        {/* No data */}
        {!loading && !matrix && !error && (
          <div className="text-center py-20 text-slate-500">
            <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Select a condo to view the permission matrix.</p>
          </div>
        )}
      </div>
    </div>
  );
}
